import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, Navigate, Routes, Route, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
    LayoutDashboard,
    BookOpen,
    Package,
    MessageSquare,
    ShoppingBag,
    LogOut,
    Pencil,
    Trash2,
    Plus,
    ArrowLeft,
    Users,
    Image as ImageIcon,
    Copy,
    Tag,
    UploadCloud,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import CategoriesTab from "@/pages/admin/CategoriesTab";
import CourseCategoriesDialog from "@/pages/admin/CourseCategoriesDialog";
import AttributesTab from "@/pages/admin/AttributesTab";
import CouponsTab from "@/pages/admin/CouponsTab";
import TaxSettingsTab from "@/pages/admin/TaxSettingsTab";
import CustomersTab from "@/pages/admin/CustomersTab";
import ReportsTab from "@/pages/admin/ReportsTab";
import OrdersTab from "@/pages/admin/OrdersTab";
import ReviewsTab from "@/pages/admin/ReviewsTab";
import MembershipsTab from "@/pages/admin/MembershipsTab";
import SchedulingTab from "@/pages/admin/SchedulingTab";
import HeroSlidesTab from "@/pages/admin/HeroSlidesTab";
import TickersTab from "@/pages/admin/TickersTab";
import EnquiriesTab from "@/pages/admin/EnquiriesTab";
import ProductImportDialog from "@/pages/admin/ProductImportDialog";
import MediaGalleryTab from "@/pages/admin/MediaGalleryTab";
import ManagerActivityTab from "@/pages/admin/ManagerActivityTab";
import WebhookLogsTab from "@/pages/admin/WebhookLogsTab";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_62fe82b4-8a19-46f2-ac06-6bb7b1c3a21c/artifacts/jzp1y8sq_Aham-Yoga-Institute-logo.png";

function StatsRow() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
    }, []);
    const cards = [
        { label: "Courses", value: stats?.courses ?? "—", Icon: BookOpen, color: "bg-brand-50 text-brand-700" },
        { label: "Products", value: stats?.products ?? "—", Icon: Package, color: "bg-accent-50 text-accent-600" },
        { label: "Users", value: stats?.users ?? "—", Icon: Users, color: "bg-brand-50 text-brand-700" },
        { label: "Orders", value: stats?.orders ?? "—", Icon: ShoppingBag, color: "bg-brand-50 text-brand-700" },
        { label: "Paid Orders", value: stats?.paid_orders ?? "—", Icon: ShoppingBag, color: "bg-accent-50 text-accent-600" },
        { label: "Messages", value: stats?.contact_messages ?? "—", Icon: MessageSquare, color: "bg-brand-50 text-brand-700" },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="admin-stats">
            {cards.map((c) => (
                <div key={c.label} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-4">
                    <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${c.color}`}>
                        <c.Icon className="h-4 w-4" />
                    </div>
                    <div className="mt-3 font-heading text-2xl font-semibold text-ink-900">{c.value}</div>
                    <div className="text-xs uppercase tracking-wider text-ink-400">{c.label}</div>
                </div>
            ))}
        </div>
    );
}

function CoursesTab() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [categoriesOpen, setCategoriesOpen] = useState(false);

    const refresh = () => {
        api.get("/admin/courses").then(({ data }) => setItems(data)).catch(() => {});
    };
    useEffect(refresh, []);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this course?")) return;
        try {
            await api.delete(`/admin/courses/${id}`);
            toast.success("Course deleted");
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                <h2 className="font-heading text-2xl font-semibold">All Courses</h2>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setCategoriesOpen(true)}
                        className="rounded-full"
                        data-testid="admin-manage-course-categories-btn"
                    >
                        <Tag className="h-4 w-4 mr-1" /> Manage categories
                    </Button>
                    <Button
                        onClick={() => navigate("/admin/courses/new")}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                        data-testid="admin-new-course-btn"
                    >
                        <Plus className="h-4 w-4 mr-1" /> New course
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-paper-alt text-ink-700 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Title</th>
                            <th className="text-left px-4 py-3 hidden md:table-cell">Level</th>
                            <th className="text-left px-4 py-3 hidden md:table-cell">Duration</th>
                            <th className="text-right px-4 py-3">Price</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((c) => (
                            <tr key={c.id} className="border-t border-gray-100" data-testid={`admin-course-row-${c.id}`}>
                                <td className="px-4 py-3 font-medium text-ink-900">{c.title}</td>
                                <td className="px-4 py-3 hidden md:table-cell text-ink-700">{c.level}</td>
                                <td className="px-4 py-3 hidden md:table-cell text-ink-700">{c.duration}</td>
                                <td className="px-4 py-3 text-right font-semibold text-brand-600">
                                    ₹{c.price.toLocaleString("en-IN")}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-2">
                                        <button
                                            onClick={() => navigate(`/admin/courses/${c.id}/edit`)}
                                            className="h-8 w-8 rounded-lg bg-paper-alt hover:bg-brand-50 text-brand-700 flex items-center justify-center"
                                            data-testid={`admin-course-edit-${c.id}`}
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            onClick={() => onDelete(c.id)}
                                            className="h-8 w-8 rounded-lg bg-paper-alt hover:bg-accent-50 text-accent-600 flex items-center justify-center"
                                            data-testid={`admin-course-delete-${c.id}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-10 text-ink-700">No courses yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <CourseCategoriesDialog open={categoriesOpen} onOpenChange={setCategoriesOpen} />
        </div>
    );
}

function ProductsTab() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [importOpen, setImportOpen] = useState(false);

    const refresh = () => {
        api.get("/admin/products").then(({ data }) => setItems(data)).catch(() => {});
    };
    useEffect(refresh, []);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        try {
            await api.delete(`/admin/products/${id}`);
            toast.success("Product deleted");
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-2xl font-semibold">All Products</h2>
                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setImportOpen(true)}
                        className="rounded-full"
                        data-testid="admin-import-products-btn"
                    >
                        <UploadCloud className="h-4 w-4 mr-1.5" /> Import CSV
                    </Button>
                    <Button
                        onClick={() => navigate("/admin/products/new")}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                        data-testid="admin-new-product-btn"
                    >
                        <Plus className="h-4 w-4 mr-1" /> New product
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-paper-alt text-ink-700 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Name</th>
                            <th className="text-left px-4 py-3 hidden md:table-cell">Category</th>
                            <th className="text-right px-4 py-3 hidden md:table-cell">Rating</th>
                            <th className="text-right px-4 py-3">Price</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-gray-100" data-testid={`admin-product-row-${p.id}`}>
                                <td className="px-4 py-3 font-medium text-ink-900">{p.name}</td>
                                <td className="px-4 py-3 hidden md:table-cell text-ink-700">{p.category}</td>
                                <td className="px-4 py-3 hidden md:table-cell text-right text-ink-700">
                                    {Number(p.rating).toFixed(1)}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-brand-600">
                                    ₹{p.price.toLocaleString("en-IN")}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-2">
                                        <button
                                            onClick={() => navigate(`/admin/products/${p.id}/edit`)}
                                            className="h-8 w-8 rounded-lg bg-paper-alt hover:bg-brand-50 text-brand-700 flex items-center justify-center"
                                            data-testid={`admin-product-edit-${p.id}`}
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            onClick={() => onDelete(p.id)}
                                            className="h-8 w-8 rounded-lg bg-paper-alt hover:bg-accent-50 text-accent-600 flex items-center justify-center"
                                            data-testid={`admin-product-delete-${p.id}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-10 text-ink-700">No products yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <ProductImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={refresh} />
        </div>
    );
}

function MessagesTab() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.get("/admin/contact-messages").then(({ data }) => setItems(data)).catch(() => {});
    }, []);
    return (
        <div>
            <h2 className="font-heading text-2xl font-semibold mb-5">Contact Messages</h2>
            {items.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center text-ink-700">
                    No messages yet.
                </div>
            ) : (
                <ul className="space-y-3">
                    {items.map((m) => (
                        <li key={m.id} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5" data-testid={`admin-message-${m.id}`}>
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <div className="font-heading font-semibold text-ink-900">{m.name}</div>
                                <div className="text-xs text-ink-400">{new Date(m.created_at).toLocaleString()}</div>
                            </div>
                            <a href={`mailto:${m.email}`} className="text-sm text-brand-600 hover:text-brand-700">
                                {m.email}
                            </a>
                            <p className="mt-2 text-sm text-ink-700 leading-relaxed whitespace-pre-line">{m.message}</p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function MediaTab() {
    const [items, setItems] = useState(null);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const refresh = () => {
        api.get("/admin/media").then(({ data }) => setItems(data)).catch(() => setItems([]));
    };
    useEffect(refresh, []);

    const upload = async (files) => {
        setUploading(true);
        let count = 0;
        for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
                await api.post("/admin/media/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                count += 1;
            } catch (err) {
                toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
            }
        }
        setUploading(false);
        if (count > 0) {
            toast.success(`${count} image${count === 1 ? "" : "s"} uploaded`);
            refresh();
        }
        if (inputRef.current) inputRef.current.value = "";
    };

    const onSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length) upload(files);
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this image? It will be hidden from the gallery (soft delete).")) return;
        try {
            await api.delete(`/admin/media/${id}`);
            toast.success("Image removed");
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const onCopy = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("URL copied");
        } catch {
            toast.error("Could not copy");
        }
    };

    return (
        <div data-testid="admin-media-tab">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
                <div>
                    <h2 className="font-heading text-2xl font-semibold">Media Gallery</h2>
                    <p className="text-sm text-ink-700 mt-1">
                        Upload images from your computer. They become available across LMS &amp; e-commerce modules.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={async () => {
                            if (!window.confirm("Permanently delete soft-deleted media older than 30 days?")) return;
                            try {
                                const { data } = await api.post("/admin/media/cleanup?days=30");
                                toast.success(`Cleanup complete`, {
                                    description: `${data.removed} record${data.removed === 1 ? "" : "s"} hard-deleted.`,
                                });
                            } catch (err) {
                                toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                            }
                        }}
                        variant="outline"
                        className="rounded-full border-gray-200 text-ink-700 hover:bg-paper-alt"
                        data-testid="admin-media-cleanup-btn"
                    >
                        <Trash2 className="h-4 w-4 mr-1.5" /> Cleanup &gt;30d
                    </Button>
                    <Input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={onSelect}
                        multiple
                        className="hidden"
                        data-testid="admin-media-input"
                    />
                    <Button
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                        data-testid="admin-media-upload-btn"
                    >
                        <ImageIcon className="h-4 w-4 mr-1.5" />
                        {uploading ? "Uploading…" : "Upload images"}
                    </Button>
                </div>
            </div>

            {items === null ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="aspect-square rounded-2xl bg-gray-100 animate-pulse" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-12 text-center">
                    <div className="h-14 w-14 mx-auto rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center">
                        <ImageIcon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-5 font-heading text-xl font-semibold text-ink-900">
                        No images yet
                    </h3>
                    <p className="mt-2 text-ink-700 max-w-md mx-auto">
                        Upload your first image to start building the media library.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {items.map((m) => (
                        <div
                            key={m.id}
                            className="group relative bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-soft"
                            data-testid={`media-item-${m.id}`}
                        >
                            <div className="aspect-square overflow-hidden bg-paper-alt">
                                <img
                                    src={m.public_url}
                                    alt={m.original_filename || ""}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-2.5 space-y-1">
                                <div
                                    className="text-xs font-medium text-ink-900 truncate"
                                    title={m.original_filename}
                                >
                                    {m.original_filename || "image"}
                                </div>
                                <div className="text-[10px] text-ink-700 flex items-center gap-1">
                                    <span>{Math.max(1, Math.round((m.size || 0) / 1024))} KB</span>
                                    <span>·</span>
                                    <span>{new Date(m.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                            <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                <button
                                    onClick={() => onCopy(m.public_url)}
                                    className="h-7 w-7 rounded-full bg-white/95 text-brand-700 flex items-center justify-center shadow-soft hover:bg-brand-500 hover:text-white"
                                    aria-label="Copy URL"
                                    data-testid={`media-copy-${m.id}`}
                                >
                                    <Copy className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    onClick={() => onDelete(m.id)}
                                    className="h-7 w-7 rounded-full bg-white/95 text-accent-700 flex items-center justify-center shadow-soft hover:bg-accent-500 hover:text-white"
                                    aria-label="Delete"
                                    data-testid={`media-delete-${m.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminPage() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab") || "courses";

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-ink-700">
                Loading…
            </div>
        );
    }
    if (!user || typeof user !== "object" || !["admin", "shop_manager"].includes(user.role)) {
        return <Navigate to="/" replace />;
    }

    // Shop managers only see the resources they're allowed to administer:
    // products, categories, attributes, coupons, orders, courses, schedules,
    // memberships. Admin-only tabs (customers, reviews, reports, messages,
    // media, gallery, hero, tickers, enquiries, and the Tax sub-tab) are hidden.
    const isAdmin = user.role === "admin";

    return (
        <div className="min-h-screen bg-paper-alt" data-testid="admin-page">
            <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="h-9 w-9 rounded-xl overflow-hidden bg-white ring-1 ring-gray-200 flex items-center justify-center p-0.5">
                            <img src={LOGO_URL} alt="Aham Yog" className="max-h-full max-w-full object-contain" />
                        </span>
                        <div className="leading-tight">
                            <div className="font-heading font-semibold text-ink-900">
                                {isAdmin ? "Aham Yog Admin" : "Shop Manager"}
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-700">
                                {user.email}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/"
                            className="text-sm text-ink-700 hover:text-brand-600 inline-flex items-center gap-1.5 px-3 py-2"
                            data-testid="admin-back-to-site"
                        >
                            <ArrowLeft className="h-4 w-4" /> Site
                        </Link>
                        <button
                            onClick={async () => { await logout(); navigate("/"); }}
                            className="text-sm text-ink-700 hover:text-accent-600 inline-flex items-center gap-1.5 px-3 py-2"
                            data-testid="admin-logout"
                        >
                            <LogOut className="h-4 w-4" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="flex items-center gap-3 mb-6">
                    <span className="h-10 w-10 rounded-2xl bg-brand-500 text-white flex items-center justify-center">
                        <LayoutDashboard className="h-5 w-5" />
                    </span>
                    <h1 className="font-heading text-3xl md:text-4xl font-semibold text-ink-900">Dashboard</h1>
                </div>

                <StatsRow />

                <div className="mt-10">
                    <Tabs
                        value={tabFromUrl}
                        onValueChange={(v) => {
                            const next = new URLSearchParams(searchParams);
                            if (v === "courses") next.delete("tab");
                            else next.set("tab", v);
                            setSearchParams(next, { replace: true });
                        }}
                        className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-8"
                    >
                        <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="admin-sidebar-nav">
                        <TabsList className="bg-white border border-gray-100 p-2 rounded-2xl flex flex-col items-stretch h-auto gap-1 w-full">
                            <TabsTrigger value="courses" className="rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full" data-testid="tab-courses">
                                Courses
                            </TabsTrigger>
                            <TabsTrigger value="ecommerce" className="rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full" data-testid="tab-ecommerce">
                                eCommerce
                            </TabsTrigger>
                            <TabsTrigger value="orders" className="rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full" data-testid="tab-orders">
                                Orders
                            </TabsTrigger>
                            <TabsTrigger value="customers" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-customers">
                                Customers
                            </TabsTrigger>
                            <TabsTrigger value="reviews" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-reviews">
                                Reviews
                            </TabsTrigger>
                            <TabsTrigger value="reports" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-reports">
                                Reports
                            </TabsTrigger>
                            <TabsTrigger value="messages" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-messages">
                                Messages
                            </TabsTrigger>
                            <TabsTrigger value="media" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-media">
                                Media
                            </TabsTrigger>
                            <TabsTrigger value="gallery" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-gallery">
                                Gallery
                            </TabsTrigger>
                            <TabsTrigger value="hero" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-hero">
                                Hero Slider
                            </TabsTrigger>
                            <TabsTrigger value="tickers" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-tickers">
                                Ticker
                            </TabsTrigger>
                            <TabsTrigger value="enquiries" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-enquiries">
                                Enquiries
                            </TabsTrigger>
                            <TabsTrigger value="activity" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-activity">
                                Manager Activity
                            </TabsTrigger>
                            <TabsTrigger value="webhooks" className={`rounded-xl justify-start data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4 py-2.5 w-full ${isAdmin ? "" : "hidden"}`} data-testid="tab-webhooks">
                                Webhooks
                            </TabsTrigger>
                        </TabsList>
                        </aside>

                        <div className="min-w-0 mt-6 lg:mt-0">

                        {/* Courses (nested) */}
                        <TabsContent value="courses" className="mt-6">
                            <Tabs defaultValue="courses-list">
                                <TabsList className="bg-paper-alt border border-gray-100 p-1 rounded-full flex flex-wrap h-auto gap-1">
                                    <TabsTrigger value="courses-list" className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-3 text-sm" data-testid="subtab-courses-list">
                                        Course list
                                    </TabsTrigger>
                                    <TabsTrigger value="schedule" className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-3 text-sm" data-testid="subtab-schedule">
                                        Schedule
                                    </TabsTrigger>
                                    <TabsTrigger value="memberships" className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-3 text-sm" data-testid="subtab-memberships">
                                        Memberships
                                    </TabsTrigger>
                                </TabsList>
                                <TabsContent value="courses-list" className="mt-5"><CoursesTab /></TabsContent>
                                <TabsContent value="schedule" className="mt-5"><SchedulingTab /></TabsContent>
                                <TabsContent value="memberships" className="mt-5"><MembershipsTab /></TabsContent>
                            </Tabs>
                        </TabsContent>

                        {/* eCommerce (nested) */}
                        <TabsContent value="ecommerce" className="mt-6">
                            <Tabs defaultValue="products">
                                <TabsList className="bg-paper-alt border border-gray-100 p-1 rounded-full flex flex-wrap h-auto gap-1">
                                    <TabsTrigger value="products" className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-3 text-sm" data-testid="subtab-products">
                                        Products
                                    </TabsTrigger>
                                    <TabsTrigger value="categories" className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-3 text-sm" data-testid="subtab-categories">
                                        Categories
                                    </TabsTrigger>
                                    <TabsTrigger value="attributes" className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-3 text-sm" data-testid="subtab-attributes">
                                        Attributes
                                    </TabsTrigger>
                                    <TabsTrigger value="coupons" className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-3 text-sm" data-testid="subtab-coupons">
                                        Coupons
                                    </TabsTrigger>
                                    <TabsTrigger value="tax" className={`rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-3 text-sm ${isAdmin ? "" : "hidden"}`} data-testid="subtab-tax">
                                        Tax
                                    </TabsTrigger>
                                </TabsList>
                                <TabsContent value="products" className="mt-5"><ProductsTab /></TabsContent>
                                <TabsContent value="categories" className="mt-5"><CategoriesTab /></TabsContent>
                                <TabsContent value="attributes" className="mt-5"><AttributesTab /></TabsContent>
                                <TabsContent value="coupons" className="mt-5"><CouponsTab /></TabsContent>
                                {isAdmin && (
                                    <TabsContent value="tax" className="mt-5"><TaxSettingsTab /></TabsContent>
                                )}
                            </Tabs>
                        </TabsContent>

                        <TabsContent value="orders" className="mt-6"><OrdersTab /></TabsContent>
                        {isAdmin && (
                            <>
                                <TabsContent value="customers" className="mt-6"><CustomersTab /></TabsContent>
                                <TabsContent value="reviews" className="mt-6"><ReviewsTab /></TabsContent>
                                <TabsContent value="reports" className="mt-6"><ReportsTab /></TabsContent>
                                <TabsContent value="messages" className="mt-6"><MessagesTab /></TabsContent>
                                <TabsContent value="media" className="mt-6"><MediaTab /></TabsContent>
                                <TabsContent value="gallery" className="mt-6"><MediaGalleryTab /></TabsContent>
                                <TabsContent value="hero" className="mt-6"><HeroSlidesTab /></TabsContent>
                                <TabsContent value="tickers" className="mt-6"><TickersTab /></TabsContent>
                                <TabsContent value="enquiries" className="mt-6"><EnquiriesTab /></TabsContent>
                                <TabsContent value="activity" className="mt-6"><ManagerActivityTab /></TabsContent>
                                <TabsContent value="webhooks" className="mt-6"><WebhookLogsTab /></TabsContent>
                            </>
                        )}
                        </div>
                    </Tabs>
                </div>
            </main>
        </div>
    );
}
